import React, { useEffect, useState } from 'react';

/**
 * Calculate Haversine distance between two points in kilometers
 */
const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Format time duration (hours and minutes)
 */
const formatDuration = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m} Min`;
  if (m === 0) return `${h} Hr`;
  return `${h} Hr ${m} Min`;
};

/**
 * Format ETA time
 */
const formatETA = (hours) => {
  const now = new Date();
  now.setHours(now.getHours() + Math.floor(hours));
  now.setMinutes(now.getMinutes() + Math.round((hours - Math.floor(hours)) * 60));
  return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const RVGBottomCard = ({ data, userLocation, onClose, onNavigate, onFeedbackClick }) => {
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [eta, setEta] = useState(null);
  const [showCopyNudge, setShowCopyNudge] = useState(false);
  const [showCoordsCopyNudge, setShowCoordsCopyNudge] = useState(false);

  useEffect(() => {
    if (userLocation && data?.location) {
      const dist = haversineDistance(
        userLocation.lat,
        userLocation.lng,
        data.location.lat,
        data.location.lon || data.location.long
      );

      const speedKmh = 30;
      const durationHours = dist / speedKmh;

      setDistance(dist);
      setDuration(durationHours);
      setEta(formatETA(durationHours));
    } else {
      setDistance(null);
      setDuration(null);
      setEta(null);
    }
  }, [userLocation, data]);

  useEffect(() => {
    if (!showCopyNudge) return;
    const timeout = setTimeout(() => {
      setShowCopyNudge(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [showCopyNudge]);

  useEffect(() => {
    if (!showCoordsCopyNudge) return;
    const timeout = setTimeout(() => {
      setShowCoordsCopyNudge(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [showCoordsCopyNudge]);

  if (!data) return null;

  const displayName = data.entity_name || data.display_name || data.name || 'Unknown Location';
  const displayAddress = data.display_text || data.entity_address || data.display_address || data.address || '';
  const isInternalUrlCard = data.source === 'INTERNAL_URL';
  const displayLocality = isInternalUrlCard && data.locality ? String(data.locality).trim() : '';
  const shouldShowAddress = displayAddress && displayName !== displayAddress;

  const lat = data.location?.lat ?? data.clickedLocation?.lat;
  const lng = data.location?.lon || data.location?.long || data.clickedLocation?.long;
  const hasCoords = typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng);
  const coordsText = hasCoords ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '';

  const handleCopyAddress = async () => {
    if (!displayAddress) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(displayAddress);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = displayAddress;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setShowCopyNudge(true);
    } catch (error) {
      // Silently ignore copy failures
    }
  };

  const handleCopyCoords = async () => {
    if (!coordsText) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(coordsText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = coordsText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setShowCoordsCopyNudge(true);
    } catch (error) {
      // Silently ignore copy failures
    }
  };

  return (
    <div className="rvg-bottom-card">
      <button className="rvg-card-close-btn" onClick={onClose} aria-label="Close">
        <img src="/icons/rvgCross.png" alt="" />
      </button>

      <div className="rvg-bottom-card-content">
        <div className="rvg-card-main">
          {!isInternalUrlCard && (
            <div className="rvg-card-thumbnail">
              <img src="/icons/_v1rvgCardMap.png" alt="" />
            </div>
          )}

          <div className="rvg-card-info">
            {isInternalUrlCard ? (
              <>
                <div className="rvg-card-name rvg-card-name-internal">Consignee Location</div>

                <div className="rvg-card-address-wrapper">
                  <div className="rvg-card-address rvg-card-detail-internal">
                    Address: {displayAddress || '-'}
                  </div>
                </div>

                <div className="rvg-card-address-wrapper">
                  <div className="rvg-card-address rvg-card-detail-internal">
                    Locality: {displayLocality || '-'}
                  </div>
                </div>

                <div className="rvg-card-coords-wrapper">
                  <div className="rvg-card-coords rvg-card-detail-internal">
                    Lat/Long: {coordsText || '-'}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="rvg-card-name">{displayName}</div>

                {shouldShowAddress && (
                  <div className="rvg-card-address-wrapper">
                    <div
                      className="rvg-card-address"
                      onClick={handleCopyAddress}
                      title="Click to copy address"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleCopyAddress();
                        }
                      }}
                    >
                      {displayAddress}
                    </div>
                    {showCopyNudge && (
                      <div className="rvg-card-copy-nudge" role="status" aria-live="polite">
                        Copied to clipboard
                      </div>
                    )}
                  </div>
                )}

                {distance !== null && (
                  <div className="rvg-card-meta">
                    <span className="rvg-card-meta-item">{formatDuration(duration)}</span>
                    <span className="rvg-card-meta-dot" aria-hidden="true" />
                    <span className="rvg-card-meta-item">{eta} ETA</span>
                    <span className="rvg-card-meta-dot" aria-hidden="true" />
                    <span className="rvg-card-meta-item">{distance.toFixed(1)} KM</span>
                  </div>
                )}

                {coordsText && (
                  <>
                    <div className="rvg-card-divider" aria-hidden="true" />
                    <div className="rvg-card-coords-wrapper">
                      <div
                        className="rvg-card-coords"
                        onClick={handleCopyCoords}
                        title="Click to copy coordinates"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleCopyCoords();
                          }
                        }}
                      >
                        {coordsText}
                      </div>
                      {showCoordsCopyNudge && (
                        <div className="rvg-card-copy-nudge" role="status" aria-live="polite">
                          Copied to clipboard
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {onNavigate && hasCoords && (
          <button className="rvg-card-directions-btn" onClick={onNavigate} aria-label="Navigate">
            <img src="/icons/directionRvg.png" alt="Navigate" />
          </button>
        )}
      </div>

      <button 
        className="rvg-card-flag" 
        onClick={onFeedbackClick}
        aria-label="Report an issue"
        title="Report an issue"
      >
        <img src="/icons/flag.png" alt="" />
      </button>
    </div>
  );
};

export default RVGBottomCard;

